import { ApiResult, apiUrl } from '../../../api';
import {
  PrioShareDto,
  PrioShareFileDto,
  PrioSharePublishDto,
} from '../../../models/PrioShare';
import { getAccessToken } from '../../../store/authEffect';
import fetchWithRetry from '../../../util/fetchWithRetry';

/**
 * Get all PrioShare objects of a project extension
 */
export const apiGetPrioShares = async (
  projectExtensionId: string,
  includeExpired: boolean = false
): Promise<ApiResult<PrioShareDto[]>> => {
  const params = new URLSearchParams();
  if (includeExpired) params.append('includeExpired', 'true');

  const queryString = params.toString();
  const url = `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare${queryString ? `?${queryString}` : ''}`;

  const result = await fetchWithRetry(url, {
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      'Content-Type': 'application/json',
    },
    method: 'GET',
  });

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Deactivate a PrioShare (sets state to Expired)
 */
export const apiDeactivatePrioShare = async (
  projectExtensionId: string,
  prioShareId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare/${prioShareId}/deactivateShare`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }
  );

  return { result };
};

/**
 * Create a PrioShare draft
 */
export const apiCreatePrioShareDraft = async (
  projectExtensionId: string
): Promise<ApiResult<PrioShareDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare/draft`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Publish a PrioShare (sets state from draft to active)
 */
export const apiPublishPrioShare = async (
  dto: PrioSharePublishDto
): Promise<ApiResult<PrioShareDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${dto.projectExtensionId}/PrioShare/${dto.prioShareId}/publishShare`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify({
        name: dto.name,
        expirationDate: dto.expirationDate,
        recipients: dto.recipients,
        notificationEmails: dto.notificationEmails,
        messageText: dto.messageText,
        senderType: dto.senderType,
        senderMailboxId: dto.senderMailboxId,
      }),
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Request a SAS upload URL from the backend
 */
const apiRequestPrioShareUploadUrl = async (
  projectExtensionId: string,
  prioShareId: string,
  fileName: string,
  contentType: string
): Promise<{ sasUrl: string; blobFileId: string } | null> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare/${prioShareId}/files/uploadUrl`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      } as Record<string, string>,
      method: 'POST',
      body: JSON.stringify({ fileName, contentType }),
    }
  );
  if (result.status >= 200 && result.status < 300) {
    return result.json();
  }
  return null;
};

/**
 * Upload a file directly to Azure Blob Storage via SAS URL with optional progress callback
 */
const uploadToAzureBlob = (
  sasUrl: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', sasUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    if (onProgress) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Azure upload failed: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error('Azure upload network error'));
    xhr.send(file);
  });
};

/**
 * Register an uploaded blob file with the backend
 */
const apiRegisterPrioShareFile = async (
  projectExtensionId: string,
  prioShareId: string,
  blobFileId: string,
  fileName: string,
  alternativeFilename?: string
): Promise<ApiResult<PrioShareFileDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare/${prioShareId}/files/register`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      } as Record<string, string>,
      method: 'POST',
      body: JSON.stringify({ blobFileId, fileName, alternativeFilename }),
    }
  );
  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return { result, data };
};

/**
 * Upload a file to a PrioShare draft via SAS URL direct upload (3-step process):
 * 1. Request SAS URL from backend
 * 2. Upload file directly to Azure Blob Storage
 * 3. Register the uploaded blob with the backend
 */
export const apiUploadPrioShareFile = async (
  projectExtensionId: string,
  prioShareId: string,
  file: File,
  alternativeFilename?: string,
  onProgress?: (percent: number) => void
): Promise<ApiResult<PrioShareFileDto>> => {
  const urlResponse = await apiRequestPrioShareUploadUrl(
    projectExtensionId,
    prioShareId,
    file.name,
    file.type || 'application/octet-stream'
  );

  if (!urlResponse) {
    return { result: new Response(null, { status: 500 }), data: null };
  }

  try {
    await uploadToAzureBlob(urlResponse.sasUrl, file, onProgress);
  } catch {
    return { result: new Response(null, { status: 500 }), data: null };
  }

  return apiRegisterPrioShareFile(
    projectExtensionId,
    prioShareId,
    urlResponse.blobFileId,
    file.name,
    alternativeFilename
  );
};

/**
 * Delete a file from a PrioShare draft
 */
export const apiDeletePrioShareFile = async (
  projectExtensionId: string,
  prioShareId: string,
  prioShareFileId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare/${prioShareId}/files/${prioShareFileId}`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    }
  );

  return { result };
};

/**
 * Delete a PrioShare draft
 * TODO: Backend endpoint not yet implemented
 */
export const apiDeletePrioShareDraft = async (
  projectExtensionId: string,
  prioShareId: string
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare/${prioShareId}/draft`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'DELETE',
    }
  );

  return { result };
};

/**
 * Save a PrioShare draft (update form data without publishing)
 * TODO: Backend endpoint not yet implemented
 */
export const apiSavePrioShareDraft = async (
  projectExtensionId: string,
  prioShareId: string,
  dto: {
    name: string;
    expirationDate: string;
    recipients: { email: string; link?: string }[];
    notificationEmails: string[];
    messageText: string;
    senderType: string;
    senderMailboxId: string;
  }
): Promise<ApiResult<void>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare/${prioShareId}/draft`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(dto),
    }
  );

  return { result };
};

/**
 * Recreate an expired PrioShare as a new draft including recipients, text data and files
 */
export const apiRecreatePrioShare = async (
  projectExtensionId: string,
  prioShareId: string
): Promise<ApiResult<PrioShareDto>> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare/${prioShareId}/recreate`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
    }
  );

  const data =
    result.status >= 200 && result.status < 300 ? await result.json() : null;
  return {
    result,
    data,
  };
};

/**
 * Download one or more PrioShare files as a ZIP archive.
 * Returns true on success, throws on failure.
 */
export const apiDownloadPrioShareFiles = async (
  projectExtensionId: string,
  prioShareId: string,
  prioShareFileIds: string[]
): Promise<boolean> => {
  const result = await fetchWithRetry(
    `${apiUrl}/prioshare/projectExtension/${projectExtensionId}/PrioShare/${prioShareId}/download`,
    {
      headers: {
        Authorization: `Bearer ${await getAccessToken()}`,
        'Content-Type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify({ prioShareFileIds }),
    }
  );

  if (result.status >= 200 && result.status < 300) {
    const blob = await result.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'download.zip';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    return true;
  }

  throw new Error(`Download failed with status ${result.status}`);
};
